import { createSelector } from '@reduxjs/toolkit';
import { SupportedLocales } from '@/models/localization.model';
import { getInspectorBlock } from '../helpers/blockInspectorLocaleMerge';
import { RootState } from '@/store';
import { CONTENT_PAGES_SLICE_NAME, ContentPagesState } from './models';

export const contentPagesSelector = (state: RootState): ContentPagesState => state[CONTENT_PAGES_SLICE_NAME];

export const contentPagesListSelector = createSelector(contentPagesSelector, (state) => state.list);
export const contentPagesEditorSelector = createSelector(contentPagesSelector, (state) => state.editor);
export const contentPagesAuxSelector = createSelector(contentPagesSelector, (state) => state.aux);
export const contentPagesAutosaveSelector = createSelector(contentPagesSelector, (state) => state.autosave);

export const selectSchemaByType = createSelector(contentPagesAuxSelector, (aux) =>
  Object.fromEntries(aux.schemas.map((schema) => [schema.type, schema])),
);

export const selectSelectedBlock = createSelector(contentPagesEditorSelector, (editor) => {
  if (!editor.page || !editor.selectedBlockId) return null;
  return editor.page.blocks.find((block) => block.id === editor.selectedBlockId) ?? null;
});

export const selectBlogCategoryOptions = (activeLocale: SupportedLocales) =>
  createSelector(contentPagesAuxSelector, (aux) =>
    aux.blogCategories.map((category) => {
      const name =
        category.locale[activeLocale]?.name ??
        category.locale[SupportedLocales.UK]?.name ??
        category.locale[SupportedLocales.RU]?.name ??
        category.id;
      return { value: category.id, label: name || category.id };
    }),
  );

export const selectCanAutosave = createSelector(contentPagesSelector, (state) => {
  const { page } = state.editor;
  const { hasUnsavedChanges, isPatching } = state.autosave;
  return Boolean(page) && hasUnsavedChanges && !isPatching;
});

export const selectSelectedSchema = createSelector(
  selectSelectedBlock,
  selectSchemaByType,
  (selectedBlock, schemaByType) => (selectedBlock ? schemaByType[selectedBlock.type]?.schema : undefined),
);

export const selectInspectorBlock = (activeLocale: SupportedLocales, locales: SupportedLocales[]) =>
  createSelector(
    contentPagesEditorSelector,
    selectSelectedBlock,
    selectSelectedSchema,
    (editor, selectedBlock, selectedSchema) => {
      if (!selectedBlock || !editor.page?.localeBlocks) return null;
      const idx = editor.page.blocks.findIndex((b) => b.id === selectedBlock.id);
      if (idx < 0) return selectedBlock;
      return (
        getInspectorBlock(selectedBlock, idx, editor.page.localeBlocks, locales, selectedSchema ?? undefined) ??
        selectedBlock
      );
    },
  );
